import React, { useMemo, useCallback, useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography
} from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import AttachmentIcon from "@mui/icons-material/Attachment"
import DeleteIcon from "@mui/icons-material/Delete"
import DownloadIcon from "@mui/icons-material/Download"
import InfoIcon from "@mui/icons-material/Info"
import { I18n, Translate } from "utils/i18n-shim"
import { v4 as uuidv4 } from "uuid"
import Dropzone from "react-dropzone"
import isNil from "lodash/isNil"
import { filesize } from "filesize"

import { MAX_FILE_SIZE } from "constants.js"
import useConfirm from "hooks/useConfirm"

/**
 * @param {'warning' | 'error'} variant
 */
const getDetailsTextColor = (variant) => {
  if (variant === "error") {
    return "red"
  }

  return "inherit"
}

/**
 *
 * @param {object} props
 * @param {{fileName: string; details: string[]}} props.value
 * @param {'warning' | 'error'} props.variant
 */
const DropzoneHelperTextComponent = ({ value, variant }) => {
  return (
    <Box mb={2.25}>
      <Typography variant="h3">{value.fileName}</Typography>
      {value.details.map((text, index) => {
        return (
          <Box
            key={`${text}-${index}`}
            sx={{ color: getDetailsTextColor(variant) }}
          >
            {text}
          </Box>
        )
      })}
    </Box>
  )
}

export const DropzoneHelperText = React.memo(DropzoneHelperTextComponent)

const UploadedFile = ({
  file,
  onDelete,
  onDeleteSuccess,
  onPreview,
  onDownload,
  compactView
}) => {
  const confirm = useConfirm()
  const [isDeleting, setIsDeleting] = useState(false)
  const handlePreview = useCallback(() => {
    if (onPreview) {
      onPreview(file)
    }
  }, [onPreview, file])
  const fileName = file.file_name ?? file.name

  return (
    <ListItem
      sx={{
        padding: compactView ? "12px" : "16px",
        borderRadius: "8px",
        backgroundColor: "#FAFAFA"
      }}
    >
      <ListItemIcon>
        <AttachmentIcon />
      </ListItemIcon>
      <ListItemText
        onClick={handlePreview}
        primary={
          <Stack sx={{ cursor: onPreview ? "pointer" : "inherit" }}>
            <Box
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "75%",
                fontWeight: 500,
                fontSize: compactView ? 12 : 14
              }}
            >
              {fileName}
            </Box>
            {!isNil(file.size) && (
              <Typography fontSize={compactView ? 10 : 12}>
                {filesize(file.size ?? 0)}
              </Typography>
            )}
          </Stack>
        }
      />
      <ListItemSecondaryAction>
        <Stack direction="row" pr={2} gap={2}>
          {onDownload && !!file.id && (
            <IconButton size="large" onClick={() => onDownload(file)}>
              <DownloadIcon />
            </IconButton>
          )}
          <IconButton
            edge="end"
            aria-label={I18n.t("remove")}
            title={I18n.t("remove")}
            disabled={isDeleting}
            onClick={() =>
              confirm(I18n.t("delete.sure", { entity: fileName }), {
                onConfirm: async () => {
                  try {
                    setIsDeleting(true)
                    await onDelete(file)
                    if (onDeleteSuccess) {
                      await onDeleteSuccess(file)
                    }
                  } finally {
                    setIsDeleting(false)
                  }
                }
              })
            }
            size="large"
          >
            {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />}
          </IconButton>
        </Stack>
      </ListItemSecondaryAction>
    </ListItem>
  )
}

const FilesDropzoneCompactComponent = ({
  getInputProps,
  getRootProps,
  name,
  hint
}) => {
  return (
    <List sx={{ p: 0 }}>
      <ListItem
        {...getRootProps()}
        sx={{
          cursor: "pointer",
          padding: "10px",
          border: "1px solid",
          borderRadius: "8px",
          borderColor: "#bdbdbd",
          backgroundColor: "#FAFAFA"
        }}
      >
        <input {...getInputProps({ name })} />
        <ListItemIcon>
          <CloudUploadIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <Stack>
              <Box
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: "75%",
                  fontSize: 12,
                  fontWeight: 500
                }}
              >
                <Translate value="upload-files" />
              </Box>

              <List sx={{ p: 0 }}>
                <ListItem sx={{ p: 0 }}>
                  <ListItemIcon sx={{ mr: -2 }}>
                    <InfoIcon fontSize="1rem" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography fontSize={10}>{hint}</Typography>}
                  />
                </ListItem>
              </List>
            </Stack>
          }
        />
      </ListItem>
    </List>
  )
}

/**
 * @param {object} props
 * @param {(file: any) => void} props.uploadAsyncFn
 * @param {object[]} props.value
 * @param {(files: object[]) => void} props.onChange
 * @param {(file: any) => void} props.onDelete
 * @param {(file: any) => void} [props.onDeleteSuccess]
 * @param {(file: any) => void} [props.onPreview]
 * @param {(file: any) => void} [props.onDownload]
 * @param {React.ReactNode} props.hint
 * @param {{[k: string]: string[]}} props.accept
 * @param {boolean} [props.multiple]
 * @param {boolean} [props.error]
 * @param {string} [props.helperText]
 * @param {React.ReactNode} [props.embeddedErrors]
 */
const FilesDropzoneComponent = ({
  onDrop,
  uploadAsyncFn,
  value,
  onChange,
  onDelete,
  onDeleteSuccess,
  onPreview,
  onDownload,
  accept,
  name,
  hint,
  disabled,
  error = false,
  helperText,
  multiple = false,
  compactView = false,
  embeddedErrors = null
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState([])
  const validTypes = useMemo(() => {
    return Object.values(accept).flatMap((types) => {
      return types.map((type) => type.replace(/^\./, ""))
    })
  }, [accept])

  const handleDrop = async (accepted, rejected) => {
    setIsLoading(true)

    setErrors(
      rejected.map((file) => ({
        id: uuidv4(),
        name: file.name,
        error: I18n.t("errors.invalid-file-type-upload", {
          type: file.type,
          valid_types: validTypes
        })
      }))
    )

    try {
      const results = await Promise.all(
        accepted.map((file) => {
          return uploadAsyncFn(file).catch((error) => {
            setErrors((errors) =>
              errors.concat({
                id: uuidv4(),
                fileName: file.name,
                details: Object.values(error.response.data.errors).flat()
              })
            )
          })
        })
      )

      return onChange(results.filter(Boolean))
    } finally {
      setIsLoading(false)
    }
  }

  const files = (multiple ? value : [value]).filter(Boolean)
  const showDropzone = multiple || !value

  return (
    <Box>
      <Stack gap={3}>
        {showDropzone && (
          <Dropzone
            multiple={multiple}
            disabled={isLoading || disabled}
            accept={accept}
            onDrop={onDrop || handleDrop}
            maxSize={MAX_FILE_SIZE}
          >
            {({ getRootProps, getInputProps, isDragActive }) => {
              if (compactView) {
                return (
                  <FilesDropzoneCompactComponent
                    getInputProps={getInputProps}
                    getRootProps={getRootProps}
                    name={name}
                    hint={hint}
                  />
                )
              }

              return (
                <Box
                  {...getRootProps()}
                  sx={{
                    border: "1px dashed #dfdfdf",
                    borderRadius: "8px",
                    padding: "32px",
                    backgroundColor: "#fff",
                    position: "relative",
                    ...(isDragActive
                      ? { borderColor: "primary.main", borderStyle: "solid" }
                      : {}),
                    ...(error ? { borderColor: "error.main" } : {})
                  }}
                >
                  <input {...getInputProps({ name })} />
                  <Box
                    sx={{
                      display: "grid",
                      height: "100%",
                      placeItems: "center"
                    }}
                  >
                    {isLoading && (
                      <Box position="absolute">
                        <CircularProgress color="primary" />
                      </Box>
                    )}
                    <Stack
                      gap={3}
                      alignItems="center"
                      sx={{ opacity: isLoading ? 0.1 : 1 }}
                    >
                      <CloudUploadIcon
                        sx={{
                          fontSize: 66,
                          color: error ? "error.main" : "#34384d",
                          opacity: error ? 0.8 : 0.4
                        }}
                      />
                      <Stack gap={1} alignItems="center">
                        <Typography
                          variant="h5"
                          color={error ? "error" : "inherit"}
                        >
                          <Translate value="drag-n-drop" />
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={400}
                          color={error ? "error" : "inherit"}
                        >
                          {hint}
                        </Typography>
                      </Stack>
                      <Button
                        disabled={isLoading || disabled}
                        variant="outlined"
                        color={error ? "error" : "inherit"}
                      >
                        <Translate value="select-file" />
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              )
            }}
          </Dropzone>
        )}
        {!!errors.length && (
          <Box>
            <Typography variant="h5">
              <Translate value="failed" number={errors.length} />
            </Typography>
            {errors.map((error) => (
              <DropzoneHelperText
                key={error.id}
                variant="error"
                value={error}
              />
            ))}
          </Box>
        )}
      </Stack>
      {!!files.length && (
        <List sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {files.map((file, index) => (
            <UploadedFile
              key={file.id ?? `index-${index}`}
              file={file}
              onDelete={(file) => {
                onDelete(file, index)
              }}
              onDeleteSuccess={onDeleteSuccess}
              onPreview={onPreview}
              onDownload={onDownload}
              compactView={compactView}
            />
          ))}
        </List>
      )}
      <Stack gap={2}>
        {embeddedErrors}
        {error && helperText && (
          <Typography color="error">{helperText}</Typography>
        )}
      </Stack>
    </Box>
  )
}

export const FilesDropzone = React.memo(FilesDropzoneComponent)
